import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_URL } from '../constants/apiConstants';
import axiosInstance from '../api/axios';

const initialState: any = {
  menuPlans: [],
  loading: false,
  error: null,
};

// Async Thunks for fetching and saving Menu Plans
export const fetchInitiateMenuPlans = createAsyncThunk(
  'initiateMenuPlan/fetchInitiateMenuPlans',
  async (payload: object) => {
    const response: any = await axiosInstance.post(`${BASE_URL}/api/v1/InitiateMenuPlan/GetInitiateMenuPlanRegular`, payload);
    return response?.data?.data?.daypartmenulist;
  }
);

export const fetchInitiateMenuPlanRegularProgram = createAsyncThunk(
  'iteamInitiateMenuPlan/fetchInitiateMenuPlanRegularProgram',
  async (payload: object) => {
    const response: any = await axiosInstance.post(`${BASE_URL}/api/v1/ItemWisePlanning/InitiateMenuPlanItemWise/GetInitiateMenuPlanRegularItemWise`, payload);
    return response?.data?.data?.daypartmenulist || [];
  }
);

export const saveInitiateMenuPlanRegularEvent = createAsyncThunk(
  'iteamInitiateMenuPlan/saveInitiateMenuPlanRegularEvents',
  async (payload: object) => {
    const response: any = await axiosInstance.post(`${BASE_URL}/api/v1/ItemWisePlanning/InitiateMenuPlanItemWise/SaveInitiateMenuPlanRegularItemWise`, payload);
    return response?.data?.data?.daypartmenulist || [];
  }
);
const iteamInitiateMenuPlanSlice = createSlice({
  name: 'IteamInitiateMenuPlan',
  initialState,
  reducers: {
    // Custom reducer to clear the menuPlans array
    clearMenuPlans: (state) => {
      state.menuPlans = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitiateMenuPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInitiateMenuPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.menuPlans = action.payload; // Assuming the payload is an array of menu plans
      })
      .addCase(fetchInitiateMenuPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });    
    // Regular Program
    builder
      .addCase(fetchInitiateMenuPlanRegularProgram.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInitiateMenuPlanRegularProgram.fulfilled, (state, action) => {
        state.loading = false;
        // If the payload is a single daypart (from daypartid filter), merge it with existing state
        if (Array.isArray(action.payload) && action.payload.length === 1 && state.menuPlans.length > 1) {
          const updatedDaypart = action.payload[0];
          state.menuPlans = state.menuPlans.map((plan: any) => {
            if (plan.daypartcode === updatedDaypart.daypartcode) {
              // Preserve menu item order by merging items based on itemcode
              const existingMenuItems = plan.menuitemlist || [];
              const updatedMenuItems = updatedDaypart.menuitemlist || [];
              
              // Create a map of updated items by itemcode for quick lookup
              const updatedItemsMap = new Map();
              updatedMenuItems.forEach((item: any) => {
                if (item.itemcode) {
                  updatedItemsMap.set(item.itemcode, item);
                }
              });
              
              // Preserve order by updating existing items and appending new ones
              const mergedMenuItems = existingMenuItems.map((existingItem: any) => {
                if (existingItem.itemcode && updatedItemsMap.has(existingItem.itemcode)) {
                  return updatedItemsMap.get(existingItem.itemcode);
                }
                return existingItem; // Keep unchanged if not in update
              });
              
              // Add any new items that weren't in existing list
              updatedMenuItems.forEach((updatedItem: any) => {
                if (updatedItem.itemcode && !existingMenuItems.some((existingItem: any) => existingItem.itemcode === updatedItem.itemcode)) {
                  mergedMenuItems.push(updatedItem);
                }
              });
              
              return {
                ...plan,
                ...updatedDaypart,
                menuitemlist: mergedMenuItems
              };
            }
            return plan; // Keep unchanged dayparts
          });
        } else {
          // Full data load (initial or complete refresh)
          state.menuPlans = action.payload;
        }
      })
      .addCase(fetchInitiateMenuPlanRegularProgram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
       builder
      .addCase(saveInitiateMenuPlanRegularEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveInitiateMenuPlanRegularEvent.fulfilled, (state, action) => {
        state.loading = false;
        // Only update when payload is a non-empty array with a first item
        if (Array.isArray(action.payload) && action.payload.length > 0 && action.payload[0]) {
          state.menuPlans = state.menuPlans.map((plan: any) =>
            plan.daypartcode === action.payload[0].daypartcode
              ? { ...plan, ...action.payload[0] } // Update matching object
              : plan // Keep unchanged objects
          );
        }
      })
      .addCase(saveInitiateMenuPlanRegularEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

// Export the custom action `clearMenuPlans` to be dispatched
export const { clearMenuPlans } = iteamInitiateMenuPlanSlice.actions;
export default iteamInitiateMenuPlanSlice.reducer;
